import type * as THREE from 'three';

/**
 * What the frame actually cost, on screen.
 *
 * Written after a bug that had been doubling the cost of every frame for some
 * unknown length of time went unnoticed because the only instrument anybody had
 * was whether it felt smooth. It felt smooth on a 3080. The rule this exists to
 * enforce is that a performance claim should be a reading rather than an
 * impression — including, and especially, the claim that nothing is wrong.
 *
 * Two things about it are deliberate and worth not undoing:
 *
 * **It measures the main thread, not the GPU.** `performance.now()` around a
 * draw call tells you when the *command* was submitted, not when the pixels
 * were drawn — WebGL is asynchronous and the driver is free to be a whole frame
 * behind. So `render` here is submission cost, which is real and is often the
 * bottleneck, but a frame that is slow with every timer reading low is a frame
 * that is GPU-bound, and that is a diagnosis in itself: it means look at
 * resolution, lights and fill rate rather than at anything on this list.
 *
 * **It updates the DOM ten times a second, not sixty.** An overlay that rewrites
 * itself every frame is an overlay that changes the number it is reporting, and
 * text that flickers between three values is unreadable anyway. Samples
 * accumulate and the panel is rewritten on a timer.
 */

/** How long a window of samples runs before the panel is rewritten, seconds. */
const WINDOW = 0.5;

/** The stages worth timing separately. Anything else lands in the remainder. */
export type Stage = 'sim' | 'physics' | 'actors' | 'hud' | 'render';

const STAGES: readonly Stage[] = ['sim', 'physics', 'actors', 'hud', 'render'];

interface Window {
  frames: number;
  frameMs: number;
  worstMs: number;
  stages: Record<Stage, number>;
}

const emptyWindow = (): Window => ({
  frames: 0,
  frameMs: 0,
  worstMs: 0,
  stages: { sim: 0, physics: 0, actors: 0, hud: 0, render: 0 },
});

export class PerfOverlay {
  private readonly root: HTMLElement;
  private window = emptyWindow();
  private since = 0;
  private open: boolean;

  /** Where the currently-open stage started, or null between stages. */
  private stageStart: number | null = null;
  private stage: Stage | null = null;
  /** This frame's totals, folded into the window when the frame ends. */
  private readonly frameStages: Record<Stage, number> = {
    sim: 0, physics: 0, actors: 0, hud: 0, render: 0,
  };

  constructor(visible = false) {
    this.open = visible;
    this.root = document.createElement('div');
    this.root.id = 'perf-overlay';
    this.root.hidden = !visible;
    document.body.appendChild(this.root);

    /*
     * Styled here rather than in hud.css.
     *
     * The overlay is a tool and not part of the game's furniture, and keeping it
     * self-contained means it can be dropped into a page that has no stylesheet
     * — a bare test harness, a reduced repro — and still read correctly. It is
     * also the one piece of UI that must never be affected by a change to the
     * HUD it is there to measure.
     */
    const style = document.createElement('style');
    style.textContent = `
      #perf-overlay {
        position: fixed;
        top: 8px;
        left: 8px;
        z-index: 9999;
        padding: 8px 10px;
        min-width: 190px;
        background: rgba(8, 7, 5, 0.82);
        border: 1px solid rgba(176, 141, 63, 0.35);
        border-radius: 3px;
        color: #d9c9a4;
        font: 11px/1.45 ui-monospace, 'SF Mono', Menlo, monospace;
        white-space: pre;
        pointer-events: none;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
      }
      #perf-overlay .perf-warn { color: #e0a03f; }
      #perf-overlay .perf-bad { color: #d9584a; }
      #perf-overlay .perf-dim { color: #8a7f68; }
    `;
    document.head.appendChild(style);
  }

  /**
   * A short caption — the quality tier, in practice.
   *
   * Worth having on screen rather than in the console: the first question about
   * any reading here is what settings produced it, and a screenshot from
   * somebody else's machine that does not say is a screenshot that has to be
   * asked about.
   */
  label = '';

  /**
   * A line of match state, under the numbers.
   *
   * Added after a stuck pre-round freeze that could not be told apart from a
   * frozen render loop by looking at it: both are a still picture and an
   * unresponsive mage. One line saying which phase the simulation thinks it is
   * in, and whether its tick is still moving, separates them instantly — a tick
   * that is not advancing is a dead loop, and a tick advancing in a phase that
   * never changes is a stuck rule.
   */
  status = '';

  get visible(): boolean {
    return this.open;
  }

  toggle(): void {
    this.open = !this.open;
    this.root.hidden = !this.open;
  }

  /**
   * Opens a stage timer. Closing the previous one is implicit.
   *
   * Nesting is not supported and is not wanted: the stages are meant to
   * partition the frame, so that what they do not account for is a number in its
   * own right. A stage inside a stage would be double-counted and the remainder
   * would go negative, which is at least a loud way to be wrong.
   */
  begin(stage: Stage): void {
    if (!this.open) return;
    this.end();
    this.stage = stage;
    this.stageStart = performance.now();
  }

  /** Closes the open stage, if any. */
  end(): void {
    if (!this.open || this.stage === null || this.stageStart === null) return;
    this.frameStages[this.stage] += performance.now() - this.stageStart;
    this.stage = null;
    this.stageStart = null;
  }

  /**
   * Ends the frame: folds it into the window, and rewrites the panel if the
   * window is up.
   *
   * `renderer.info` is read here rather than sampled over the window because it
   * is a count of what the last frame did, not an accumulator — averaging it
   * would be averaging a series of nearly-identical integers to no purpose.
   */
  sample(renderer: THREE.WebGLRenderer, frameMs: number): void {
    if (!this.open) return;
    this.end();

    const w = this.window;
    w.frames++;
    w.frameMs += frameMs;
    w.worstMs = Math.max(w.worstMs, frameMs);
    for (const stage of STAGES) {
      w.stages[stage] += this.frameStages[stage];
      this.frameStages[stage] = 0;
    }

    this.since += frameMs / 1000;
    if (this.since < WINDOW) return;
    this.since = 0;
    this.paint(renderer, w);
    this.window = emptyWindow();
  }

  private paint(renderer: THREE.WebGLRenderer, w: Window): void {
    const frames = Math.max(1, w.frames);
    const avg = w.frameMs / frames;
    const fps = 1000 / Math.max(0.001, avg);

    const info = renderer.info;
    // Straight off the canvas. `getDrawingBufferSize` wants a `Vector2` to write
    // into, which would mean importing Three for a value rather than a type and
    // allocating something every window for two integers already sitting here.
    const width = renderer.domElement.width;
    const height = renderer.domElement.height;
    const pixels = (width * height) / 1e6;

    // Accounted stages against the whole frame. What is left is everything not
    // instrumented — browser layout, input, garbage collection — and a large
    // remainder is as much a finding as a large stage.
    const accounted = STAGES.reduce((sum, s) => sum + w.stages[s], 0) / frames;

    const rows: string[] = [
      `${this.grade(avg, 16.7, 22)}${fps.toFixed(0).padStart(3)} fps  ${avg.toFixed(1)}ms</span>`,
      `<span class="perf-dim">worst   ${w.worstMs.toFixed(1)}ms</span>`,
      '',
    ];

    for (const stage of STAGES) {
      const ms = w.stages[stage] / frames;
      rows.push(`${stage.padEnd(8)}${ms.toFixed(2).padStart(6)}ms`);
    }
    rows.push(
      `<span class="perf-dim">${'other'.padEnd(8)}${(avg - accounted).toFixed(2).padStart(6)}ms</span>`,
    );
    rows.push('');
    rows.push(`${'draws'.padEnd(8)}${String(info.render.calls).padStart(6)}`);
    rows.push(`${'tris'.padEnd(8)}${this.compact(info.render.triangles).padStart(6)}`);
    rows.push(
      `<span class="perf-dim">${'geom'.padEnd(8)}${String(info.memory.geometries).padStart(6)}</span>`,
    );
    rows.push(
      `<span class="perf-dim">${'tex'.padEnd(8)}${String(info.memory.textures).padStart(6)}</span>`,
    );
    rows.push(
      `<span class="perf-dim">${'progs'.padEnd(8)}${String(info.programs?.length ?? 0).padStart(6)}</span>`,
    );
    rows.push('');
    rows.push(
      `<span class="perf-dim">${width}x${height}  ${pixels.toFixed(1)}MP</span>`,
    );
    rows.push(
      `<span class="perf-dim">dpr ${renderer.getPixelRatio().toFixed(2)}`
      + `${this.label ? `  ${this.label}` : ''}</span>`,
    );
    if (this.status) rows.push(`<span class="perf-dim">${this.status}</span>`);

    this.root.innerHTML = rows.join('\n');
  }

  /**
   * Opens a coloured span for a value against two thresholds.
   *
   * The caller closes it. Ugly, and the alternative was a helper that took the
   * text too and could not then be used for a line built from several pieces.
   */
  private grade(value: number, warn: number, bad: number): string {
    if (value >= bad) return '<span class="perf-bad">';
    if (value >= warn) return '<span class="perf-warn">';
    return '<span>';
  }

  /** 12480 as `12.5k`, because the column is six characters wide. */
  private compact(n: number): string {
    if (n < 10000) return String(n);
    if (n < 1e6) return `${(n / 1000).toFixed(1)}k`;
    return `${(n / 1e6).toFixed(2)}M`;
  }
}
